import dynamic from 'next/dynamic';
import { type FC, type ReactNode } from 'react';

import { type ClientAuthenticatedUserWithSelectedRole } from 'connect-graphql/auth';

import { type PageAuth } from '@/src/components-v2/types';
import { PageIntegration } from '@/src/config/pages';
import { IdleUserDialog } from '@/src/features/config/components/dialogs/IdleUserDialog';
import { useInitializeDatadogUser } from '@/src/features/integrations/datadog/hooks';
import { FeatureFlagProvider } from '@/src/features/integrations/statsig/providers/FeatureFlagProvider';
import { PostLoginDialog } from '@/src/features/users/components/dialogs/post-login/PostLoginDialog';
import { PageAuthType } from '@/src/middleware/pages';

import { UserContextObj } from '../context';

import { SelectedRoleProvider } from './SelectedRoleProvider';

const UserFlow = dynamic(
  () => import('@/src/features/integrations/user-flow/UserFlow').then(mod => mod.UserFlow),
  { ssr: false },
);

interface AuthenticatedUserProviderCoreProps {
  readonly auth: PageAuth<PageAuthType.Auth0> | PageAuth<PageAuthType.Lti>;
  readonly children: ReactNode;
  readonly user: ClientAuthenticatedUserWithSelectedRole;
}

export const AuthenticatedUserProviderCore: FC<AuthenticatedUserProviderCoreProps> = ({
  auth,
  children,
  user,
}) => {
  useInitializeDatadogUser({ user });
  return (
    <UserContextObj value={user}>
      <SelectedRoleProvider user={user}>
        <FeatureFlagProvider auth={auth} user={user}>
          {children}
          <PostLoginDialog env={auth.env} user={user} />
          {auth.type === PageAuthType.Auth0 &&
            process.env.NODE_ENV !== 'test' &&
            auth.page.integrations?.includes(PageIntegration.UserFlow) && (
              <UserFlow env={auth.env} user={user} />
            )}
          {auth.type === PageAuthType.Auth0 && process.env.NODE_ENV !== 'test' && (
            <IdleUserDialog key={user.id} />
          )}
        </FeatureFlagProvider>
      </SelectedRoleProvider>
    </UserContextObj>
  );
};
